import { useEffect, useState } from "react";
import axios from "axios";
import { Landmark } from "lucide-react";
import { usd } from "@/components/dash/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const when = (s) => (s ? new Date(s).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const short = (m) => (m || "").replace("cpc-btc-updown-15m-", "BTC 15m ");

const Stat = ({ label, value, testid, cls = "" }) => (
  <div className="rounded-lg border border-[#1A2338] bg-[#0F1A2E] p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest font-num text-slate-400">{label}</div>
    <div className={`font-num font-bold text-lg mt-1 ${cls}`}>{value}</div>
  </div>
);

export const PMAccountCard = ({ tick }) => {
  const [a, setA] = useState(null);
  useEffect(() => {
    axios.get(`${API}/account`).then((r) => setA(r.data)).catch(() => setA({ connected: false, reason: "Request failed" }));
  }, [tick]);
  if (!a) return null;
  if (!a.connected) {
    return (
      <section className="card p-5 sm:p-6" data-testid="pm-account-card">
        <h2 className="font-head font-bold text-lg flex items-center gap-2"><Landmark className="w-5 h-5 text-[#22D3EE]" /> My Polymarket US account</h2>
        <p className="text-sm text-amber-300 mt-2" data-testid="pm-account-error">Not available: {a.reason}</p>
      </section>
    );
  }
  const b = a.balance, s = a.btc_15m_stats;
  return (
    <section className="card p-5 sm:p-6 fade-up" data-testid="pm-account-card">
      <h2 className="font-head font-bold text-lg flex items-center gap-2"><Landmark className="w-5 h-5 text-[#22D3EE]" /> My Polymarket US account</h2>
      <p className="text-xs text-slate-400 mt-1">Live from your Polymarket US API key (kept on the server). Orders are only placed by bots you switch to real money, or bets you confirm. Updated {new Date(a.fetched_at * 1000).toLocaleTimeString()}.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
        <Stat label="Balance" value={usd(b.current ?? 0)} testid="pm-balance" />
        <Stat label="Buying power" value={usd(b.buying_power ?? 0)} testid="pm-buying-power" />
        <Stat label="BTC 15m W / L" value={`${s.resolved_wins} / ${s.resolved_losses}`} testid="pm-btc-wl" />
        <Stat label="BTC 15m realized" value={s.realized_pnl_reported == null ? "—" : usd(s.realized_pnl_reported)} testid="pm-btc-pnl"
          cls={s.realized_pnl_reported > 0 ? "text-[#34D399]" : s.realized_pnl_reported < 0 ? "text-[#F87171]" : ""} />
      </div>
      <p className="text-[11px] text-slate-500 mt-1">{s.trades} BTC 15m trades across {s.markets} markets. {s.note}</p>
      <div className="grid lg:grid-cols-2 gap-4 mt-4 text-xs">
        <div data-testid="pm-positions">
          <div className="text-[10px] uppercase tracking-widest font-num text-slate-400 mb-1">Open positions</div>
          {a.positions.length ? a.positions.map((p) => (
            <div key={p.market} className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-2 py-1.5 border-b border-[#1A2338] last:border-0 min-w-0">
              <span className="truncate min-w-0">{p.title}{p.team ? ` · ${p.team}` : ""} · {p.outcome}</span>
              <span className="font-num whitespace-nowrap">{p.contracts} · cost {usd(p.cost ?? 0)}</span>
            </div>
          )) : <div className="text-slate-500">None</div>}
        </div>
        <div data-testid="pm-trades">
          <div className="text-[10px] uppercase tracking-widest font-num text-slate-400 mb-1">Recent trades & results</div>
          {[...a.trades.map((t) => ({ ...t, kind: "trade" })), ...a.resolutions.map((r) => ({ ...r, kind: "result" }))]
            .sort((x, y) => (y.time || "").localeCompare(x.time || "")).slice(0, 10).map((r, i) => (
              <div key={i} className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-2 py-1.5 border-b border-[#1A2338] last:border-0 min-w-0" data-testid={`pm-activity-${i}`}>
                <span className="truncate min-w-0">{when(r.time)} · {short(r.market)}</span>
                <span className="font-num whitespace-nowrap">
                  {r.kind === "trade" ? `${r.action} ${r.side} @ ${Math.round((r.order_price_yes ?? 0) * 100)}¢` :
                    <span className={r.realized_change > 0 ? "text-[#34D399]" : r.realized_change < 0 ? "text-[#F87171]" : ""}>
                      Settled {r.realized_change == null ? "" : `${r.realized_change >= 0 ? "+" : ""}${usd(r.realized_change)}`}</span>}
                </span>
              </div>
            ))}
        </div>
      </div>
    </section>
  );
};
